import { QUALITY } from "./quality.ts";
import { DataUtils, FloatType, HalfFloatType, Vector3, WebGLRenderTarget, type PerspectiveCamera, type WebGLRenderer } from "three";
import { PostFx, type PostUniforms } from "./post/post-fx.ts";
import { POST_PRESETS, POST_STYLE, type PostTier } from "./post/post-model.ts";

/** Fases da sonda de sombreamento (F51): dia, crepúsculo e noite. */
export const PROBE_PHASES = { dia: 0.15, crepusculo: 0.5, noite: 0.72 } as const;
export type ProbePhase = keyof typeof PROBE_PHASES;

/** Alvo da sonda (16:9, como o e2e) e a grade dos pontos lidos: 8 × 5 = 40 pixels. */
export const PROBE_WIDTH = 320;
export const PROBE_HEIGHT = 180;
const COLS = 8;
const ROWS = 5;

/** Opções da sonda: `full`, o alvo inteiro por fase; `post` (F52), o tier do pós; `byte`, o compositor em 8 bits. */
export interface ShadingProbeOptions {
  full?: boolean;
  /** Desenha pelo pós-processamento do tier (Baixo: direto, como sem a opção). */
  post?: PostTier;
  /** Com `post`: o compositor sem meio-float (o caminho dos navegadores sem `EXT_color_buffer_float`). */
  byte?: boolean;
  /**
   * Com `post` em meio-float: também a máscara dos pixels da cena acima do limiar do bloom (a entrada
   * dele: no Alto, com os emissivos subidos), um byte por pixel (1 quente), por fase.
   */
  hot?: boolean;
}

export interface ShadingProbeResult {
  /** Largura e altura do alvo. */
  size: [number, number];
  /**
   * Como os valores saíram: `float` (alvo em ponto flutuante, codificado em sRGB 0–255 aqui, como no
   * canvas) ou `byte` (sem `EXT_color_buffer_float`: o alvo de 8 bits lineares, convertido; perde
   * precisão no escuro).
   */
  encoding: "float" | "byte";
  /** Pixels lidos (x, y a partir de baixo, como o `readPixels`). */
  points: [number, number][];
  /** RGBA (0 a 255) de cada ponto, em sequência, por fase. */
  phases: Record<ProbePhase, number[]>;
  /** Com `full`: o alvo inteiro por fase, RGBA em base64 (comparação completa fora do e2e). */
  full?: Record<ProbePhase, string>;
  /** Com `post` (F52): o tier e os efeitos desenhados. */
  post?: { tier: PostTier; effects: string[]; float: boolean };
  /** Com `hot`: a máscara dos pixels quentes da cena, em base64, por fase. */
  hot?: Record<ProbePhase, string>;
}

/**
 * Grade no miolo da tela (80 % da largura, 60 % da altura, onde ficam os objetos das vitrines), com
 * as linhas alternadas deslocadas para não alinhar com bordas retas.
 */
export function probePoints(): [number, number][] {
  const points: [number, number][] = [];
  for (let j = 0; j < ROWS; j++) {
    for (let i = 0; i < COLS; i++) {
      const u = 0.1 + (0.8 * (i + 0.5 + (j % 2) * 0.25)) / COLS;
      const v = 0.2 + (0.6 * j) / (ROWS - 1);
      points.push([Math.floor(u * PROBE_WIDTH), Math.floor(v * PROBE_HEIGHT)]);
    }
  }
  return points;
}

/** Linear (0 a 1) para sRGB 0–255, como a saída do canvas (`colorspace_fragment`). */
function srgb8(linear: number): number {
  const c = Math.min(1, Math.max(0, linear));
  return Math.round((c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055) * 255);
}

function base64(bytes: Uint8Array): string {
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(text);
}

/**
 * Sonda de sombreamento (F51, só depuração): desenha a cena uma vez por fase num alvo fixo de
 * 320 × 180 e lê 40 pixels RGBA, para o golden do e2e conferir que os shaders não mudaram (ou o
 * quanto mudaram). O alvo de um `WebGLRenderTarget` recebe a cor linear (o three só codifica sRGB no
 * canvas); em ponto flutuante, a codificação em sRGB 0–255 é feita aqui e vale no escuro como na
 * tela (8 bits lineares juntariam vários níveis de sRGB da noite num só). `render(fase, pós)` aplica a
 * fase e desenha: sem o pós, no alvo atual; com ele, pelo pós. A câmera volta à proporção de antes.
 * Com `&tempo=0` o resultado não depende do instante. `uniforms` (os da atmosfera): sem o pós, a
 * máscara do contorno ligada no alfa e os pontos (poeira) em pixels do alvo enquanto desenha (o pós de
 * tamanho fixo faz o mesmo; sem isso, o Baixo herdava a densidade da tela, 2 no WebKit, e pontos maiores).
 * Com `post` (F52, Médio ou Alto), um compositor próprio do tamanho do alvo (sem multisampling)
 * desenha cada fase e a imagem final
 * (meio-float, ou 8 bits sRGB com `byte`) é lida no lugar do alvo; o alfa sai 1.
 */
export function shadingProbe(renderer: WebGLRenderer, camera: PerspectiveCamera, render: (phase: number, post?: PostFx) => void, options: ShadingProbeOptions = {}, uniforms?: PostUniforms): ShadingProbeResult {
  const tier = options.post ?? "baixo";
  // A sonda mede o contrato da F52 (bloom, cor, vinheta) e o sombreamento dos materiais: sem os
  // efeitos cinematográficos e sem a sombra do sol (Fase 13), que a vista desenharia pelo tier da página
  // e não pelo da sonda (as fases e os tiers não ficariam comparáveis).
  const fx = tier === "baixo" ? undefined : new PostFx(renderer, { size: [PROBE_WIDTH, PROBE_HEIGHT], settings: () => ({ tier, ...POST_PRESETS[tier], light: false, aberration: false, flare: false, motion: false, reduceFlashes: false }), ...(options.byte && { caps: { float: false } }) });
  const shadows = QUALITY.shadows;
  QUALITY.shadows = false;
  fx?.bind(uniforms);
  const precise = fx ? fx.plan().float : renderer.extensions.has("EXT_color_buffer_float");
  const target = fx ? undefined : new WebGLRenderTarget(PROBE_WIDTH, PROBE_HEIGHT, precise ? { type: FloatType } : {});
  const previous = renderer.getRenderTarget();
  const aspect = camera.aspect;
  const count = PROBE_WIDTH * PROBE_HEIGHT * 4;
  const raw = !precise ? new Uint8Array(count) : fx ? new Uint16Array(count) : new Float32Array(count);
  const pixels = new Uint8Array(count);
  const points = probePoints();
  const phases = {} as Record<ProbePhase, number[]>;
  const full = {} as Record<ProbePhase, string>;
  const hot = {} as Record<ProbePhase, string>;
  const scene = options.hot && fx && precise ? new Uint16Array(count) : undefined;
  const mask = fx ? undefined : uniforms?.uOutlineMask;
  const pointScale = fx ? undefined : uniforms?.uPixelRatio;
  const screenRatio = pointScale?.value;
  // Os efeitos do compositor que desenhou (lidos antes de ele sair, no fim).
  let effects: string[] = [];
  const masked = mask?.value;
  camera.aspect = PROBE_WIDTH / PROBE_HEIGHT;
  camera.updateProjectionMatrix();
  if (mask) mask.value = 1;
  if (pointScale) pointScale.value = 1;
  try {
    for (const [name, phase] of Object.entries(PROBE_PHASES) as [ProbePhase, number][]) {
      renderer.setRenderTarget(target ?? null);
      render(phase, fx);
      if (fx) effects = fx.stats().effects;
      const read = target ?? fx!.output!;
      renderer.readRenderTargetPixels(read, 0, 0, PROBE_WIDTH, PROBE_HEIGHT, raw);
      for (let i = 0; i < count; i++) pixels[i] = encode(raw, i, read.texture.type, !!fx);
      phases[name] = points.flatMap(([x, y]) => [...pixels.subarray((y * PROBE_WIDTH + x) * 4, (y * PROBE_WIDTH + x) * 4 + 4)]);
      if (options.full) full[name] = base64(pixels);
      if (scene) {
        // A cena que o compositor desenhou (o RenderPass) continua no alvo de entrada depois do passe.
        renderer.readRenderTargetPixels(fx!.sceneTarget()!, 0, 0, PROBE_WIDTH, PROBE_HEIGHT, scene);
        const flags = new Uint8Array(PROBE_WIDTH * PROBE_HEIGHT);
        const h = (k: number) => DataUtils.fromHalfFloat(scene[k]!);
        for (let p = 0; p < flags.length; p++) flags[p] = 0.2126 * h(p * 4) + 0.7152 * h(p * 4 + 1) + 0.0722 * h(p * 4 + 2) > POST_STYLE.threshold ? 1 : 0;
        hot[name] = base64(flags);
      }
    }
  } finally {
    QUALITY.shadows = shadows;
    renderer.setRenderTarget(previous);
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
    if (mask) mask.value = masked!;
    if (pointScale) pointScale.value = screenRatio!;
    target?.dispose();
    fx?.dispose();
  }
  const post = fx && { tier, effects, float: precise };
  return { size: [PROBE_WIDTH, PROBE_HEIGHT], encoding: precise ? "float" : "byte", points, phases, ...(options.full && { full }), ...(post && { post }), ...(scene && { hot }) };
}

/**
 * Um canal lido do alvo em sRGB 0–255: ponto flutuante (o alvo da sonda, ou o meio-float do
 * compositor) é codificado aqui; 8 bits lineares (o alvo da sonda sem a extensão) são convertidos; 8
 * bits do compositor já vêm em sRGB (o alvo é `SRGB8_ALPHA8`: a leitura devolve o guardado). O alfa é
 * linear sempre.
 */
function encode(raw: Uint8Array | Uint16Array | Float32Array, i: number, type: number, composer: boolean): number {
  const v = raw[i]!;
  const alpha = i % 4 === 3;
  if (type === HalfFloatType) return alpha ? Math.round(Math.min(1, Math.max(0, DataUtils.fromHalfFloat(v))) * 255) : srgb8(DataUtils.fromHalfFloat(v));
  if (type === FloatType) return alpha ? Math.round(Math.min(1, Math.max(0, v)) * 255) : srgb8(v);
  return alpha || composer ? v : srgb8(v / 255);
}

/**
 * Pixels RGBA (0 a 255, FRACIONÁRIOS) onde caem os pontos do mundo dados, desenhando uma vez no
 * alvo da sonda (`render` desenha a cena no alvo atual), com a máscara do contorno ligada (`mask`);
 * `null` para o ponto fora da tela. O alvo é meio-float (Fase 12): a saída linear em 8 bits perdia
 * as diferenças das sombras-blob no crepúsculo escuro (o chão típico soma ~22/765 linear, e um
 * escurecimento de 20 % sumia na quantização). Só depuração e e2e.
 */
export function probeAt(renderer: WebGLRenderer, camera: PerspectiveCamera, render: () => void, points: readonly Vector3[], mask?: { value: number }): (number[] | null)[] {
  const target = new WebGLRenderTarget(PROBE_WIDTH, PROBE_HEIGHT, { type: HalfFloatType });
  const previous = renderer.getRenderTarget();
  const aspect = camera.aspect;
  // Meio-float lê em Uint16 (o three escolhe gl.HALF_FLOAT pelo tipo da textura) e converte.
  const pixel = new Uint16Array(4);
  const masked = mask?.value;
  camera.aspect = PROBE_WIDTH / PROBE_HEIGHT;
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld();
  if (mask) mask.value = 1;
  try {
    renderer.setRenderTarget(target);
    render();
    return points.map((p) => {
      const ndc = new Vector3().copy(p).project(camera);
      if (Math.abs(ndc.x) > 1 || Math.abs(ndc.y) > 1 || ndc.z > 1) return null;
      const x = Math.min(PROBE_WIDTH - 1, Math.floor(((ndc.x + 1) / 2) * PROBE_WIDTH));
      const y = Math.min(PROBE_HEIGHT - 1, Math.floor(((ndc.y + 1) / 2) * PROBE_HEIGHT));
      renderer.readRenderTargetPixels(target, x, y, 1, 1, pixel);
      // Na mesma escala de antes (0 a 255), agora com fração: os deltas pequenos sobrevivem.
      return [DataUtils.fromHalfFloat(pixel[0]!) * 255, DataUtils.fromHalfFloat(pixel[1]!) * 255, DataUtils.fromHalfFloat(pixel[2]!) * 255, DataUtils.fromHalfFloat(pixel[3]!) * 255];
    });
  } finally {
    renderer.setRenderTarget(previous);
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
    if (mask) mask.value = masked!;
    target.dispose();
  }
}
